import React, { useEffect, useRef, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Icon from '../../components/Icon';
import PriorityIcon from '../../components/PriorityIcon/PriorityIcon';
import { AddRow, Check } from '../../components/ui';
import { SubGoal, Tag, Todo } from '../../services/goalService';
import { useTranslation } from '../../i18n';
import { formatDurationShort } from '../../utils/format';
import { elapsedSecondsSince, formatClock } from '../../utils/timer';
import { addDaysToKey } from '../../utils/zonedTime';
import { dueLabel, stageRows } from './boardData';

/** What a row can ask the page to do. */
export interface RowActions {
  toggle: (todo: Todo) => void;
  rename: (todo: Todo, title: string) => void;
  setDue: (todo: Todo, due: string | null) => void;
  setTags: (todo: Todo, tagIds: string[]) => void;
  timer: (todo: Todo) => void;
  open: (todo: Todo) => void;
  remove: (todo: Todo) => void;
  moveTo: (todo: Todo, stageId: string) => void;
  /** Alt+↑/↓: one place up or down inside the stage. */
  shift: (todo: Todo, delta: -1 | 1) => void;
}

interface StageProps {
  stage: SubGoal;
  index: number;
  folded: boolean;
  onFold: (folded: boolean) => void;
  stages: SubGoal[];
  today: string;
  allTags: Tag[];
  actions: RowActions;
  /** The row whose title is being edited (E). */
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  adding: boolean;
  setAdding: (open: boolean) => void;
  onAdd: (stage: SubGoal, title: string) => Promise<unknown> | void;
  onRename: (stage: SubGoal, title: string) => void;
  onToggleDone: (stage: SubGoal) => void;
  onDelete: (stage: SubGoal) => void;
  /** Rows with a tag outside the filter are left out. */
  tagFilter: string | null;
  highlighted: boolean;
  openTodoId?: string | null;
}

/** Closes [onClose] on a click outside [ref] or Escape. */
export const useDismiss = (ref: React.RefObject<HTMLElement | null>, open: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [ref, open, onClose]);
};

/** A small menu or panel under a cell button. */
export const Pop: React.FC<{
  label: string;
  trigger: React.ReactNode;
  triggerClass?: string;
  children: (close: () => void) => React.ReactNode;
  align?: 'start' | 'end';
}> = ({ label, trigger, triggerClass = '', children, align = 'start' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = React.useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  return (
    <div className="gb-pop" ref={ref}>
      <button
        type="button"
        className={`gb-cellbtn ${triggerClass}`.trim()}
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {trigger}
      </button>
      {open && (
        <div className={`gb-pop__panel gb-pop__panel--${align}`} role="group" aria-label={label}>
          {children(close)}
        </div>
      )}
    </div>
  );
};

const RunningTime: React.FC<{ startedAt: string; base: number }> = ({ startedAt, base }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  return <>{formatClock(base + elapsedSecondsSince(startedAt))}</>;
};

interface RowProps {
  todo: Todo;
  stage: SubGoal;
  stages: SubGoal[];
  today: string;
  allTags: Tag[];
  actions: RowActions;
  editing: boolean;
  setEditingId: (id: string | null) => void;
  sortable: boolean;
  /** The task open in the drawer (T-183). */
  isOpen?: boolean;
}

const TaskLine: React.FC<RowProps> = ({ todo, stage, stages, today, allTags, actions, editing, setEditingId, sortable, isOpen }) => {
  const { t, language } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: todo.id,
    disabled: !sortable,
    data: { stageId: stage.id },
  });
  const [draft, setDraft] = useState(todo.title);
  useEffect(() => {
    if (editing) setDraft(todo.title);
  }, [editing, todo.title]);

  const running = Boolean(todo.isStarted && todo.startedAt && !todo.completed);
  const due = dueLabel(todo.dueDate, today, language, t('hedefler.today'));
  const tags = todo.tags ?? [];
  const spent = todo.totalTimeSpent ?? 0;

  const commit = () => {
    const title = draft.trim();
    setEditingId(null);
    if (title && title !== todo.title) actions.rename(todo, title);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.target !== e.currentTarget) return;
    const key = e.key.toLowerCase();
    if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      if (!todo.completed) actions.shift(todo, e.key === 'ArrowUp' ? -1 : 1);
      return;
    }
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === ' ') {
      e.preventDefault();
      actions.toggle(todo);
    } else if (key === 'e') {
      e.preventDefault();
      setEditingId(todo.id);
    } else if (key === 't' && !todo.completed) {
      e.preventDefault();
      actions.timer(todo);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      actions.open(todo);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const rows = Array.from(document.querySelectorAll<HTMLElement>('.gb-row'));
      const at = rows.indexOf(e.currentTarget);
      rows[at + (e.key === 'ArrowDown' ? 1 : -1)]?.focus();
    }
  };

  const others = stages.filter((s) => s.id !== stage.id);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`gb-row gb-grid${todo.completed ? ' is-done' : ''}${running ? ' is-live' : ''}${isDragging ? ' is-dragging' : ''}${isOpen ? ' is-open' : ''}`}
      tabIndex={0}
      data-todo-id={todo.id}
      aria-label={todo.title}
      onKeyDown={onKeyDown}
    >
      <Check
        small
        checked={todo.completed}
        label={t(todo.completed ? 'hedefler.reopen' : 'hedefler.complete', { title: todo.title })}
        onChange={() => actions.toggle(todo)}
      />
      <span className="gb-row__title">
        {todo.priority === 'high' && !todo.completed && <PriorityIcon priority="high" className="gb-row__prio" />}
        {editing ? (
          <input
            className="gb-row__input"
            autoFocus
            aria-label={t('hedefler.editTitle', { title: todo.title })}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') setEditingId(null);
            }}
          />
        ) : (
          <button
            type="button"
            className="gb-row__name"
            title={t('hedefler.editHint')}
            onClick={() => setEditingId(todo.id)}
          >
            <bdi>{todo.title}</bdi>
          </button>
        )}
      </span>
      <span className={`gb-row__time gb-col-time${running ? ' is-live' : ''}`}>
        {running ? (
          <RunningTime startedAt={todo.startedAt as string} base={spent} />
        ) : spent > 0 ? (
          formatDurationShort(spent, language)
        ) : todo.estimatedSeconds ? (
          `≈ ${formatDurationShort(todo.estimatedSeconds, language)}`
        ) : (
          ''
        )}
      </span>
      <span className="gb-col-date">
        <Pop
          label={t('hedefler.dateFor', { title: todo.title })}
          triggerClass={due ? `is-${due.tone}` : 'is-empty'}
          trigger={due ? due.text : <Icon name="calendar" aria-hidden="true" />}
        >
          {(close) => (
            <div className="gb-datepop">
              <input
                type="date"
                aria-label={t('hedefler.dateFor', { title: todo.title })}
                value={todo.dueDate ?? ''}
                onChange={(e) => {
                  actions.setDue(todo, e.target.value || null);
                  close();
                }}
              />
              <div className="gb-datepop__row">
                <button type="button" className="ui-btn ui-btn--small" onClick={() => { actions.setDue(todo, today); close(); }}>
                  {t('hedefler.today')}
                </button>
                <button
                  type="button"
                  className="ui-btn ui-btn--small"
                  onClick={() => { actions.setDue(todo, addDaysToKey(today, 1)); close(); }}
                >
                  {t('hedefler.tomorrow')}
                </button>
                {todo.dueDate && (
                  <button type="button" className="ui-btn ui-btn--small" onClick={() => { actions.setDue(todo, null); close(); }}>
                    {t('hedefler.clearDate')}
                  </button>
                )}
              </div>
            </div>
          )}
        </Pop>
      </span>
      <span className="gb-col-tag">
        <Pop
          label={t('hedefler.tagsFor', { title: todo.title })}
          triggerClass={tags.length ? '' : 'is-empty'}
          trigger={
            tags.length ? (
              <span className="gb-tags">
                <span className="gb-tag-dot" style={{ background: tags[0].color }} aria-hidden="true" />
                <bdi>{tags[0].name}</bdi>
                {tags.length > 1 && <span className="gb-tags__more">{t('tags.more', { count: tags.length - 1 })}</span>}
              </span>
            ) : (
              <Icon name="tag" aria-hidden="true" />
            )
          }
          align="end"
        >
          {() =>
            allTags.length === 0 ? (
              <p className="gb-pop__note">{t('hedefler.noTags')}</p>
            ) : (
              <div className="gb-tagpop">
                {allTags.map((tag) => {
                  const on = tags.some((x) => x.id === tag.id);
                  return (
                    <label key={tag.id} className="gb-tagpop__item">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() =>
                          actions.setTags(
                            todo,
                            on ? tags.filter((x) => x.id !== tag.id).map((x) => x.id) : [...tags.map((x) => x.id), tag.id]
                          )
                        }
                      />
                      <span className="gb-tag-dot" style={{ background: tag.color }} aria-hidden="true" />
                      <bdi>{tag.name}</bdi>
                    </label>
                  );
                })}
              </div>
            )
          }
        </Pop>
      </span>
      <span className="gb-row__end">
        <Pop
          label={t('hedefler.rowMenu', { title: todo.title })}
          trigger={<Icon name="more" aria-hidden="true" />}
          triggerClass="gb-row__more"
          align="end"
        >
          {(close) => (
            <div className="gb-menu" role="menu">
              <button type="button" role="menuitem" onClick={() => { close(); actions.open(todo); }}>
                <Icon name="open" aria-hidden="true" /> {t('hedefler.open')}
              </button>
              {!todo.completed && (
                <button type="button" role="menuitem" onClick={() => { close(); actions.timer(todo); }}>
                  <Icon name={running ? 'pause' : 'play'} aria-hidden="true" /> {t(running ? 'hedefler.stopTimer' : 'hedefler.startTimer')}
                </button>
              )}
              {others.length > 0 && (
                <>
                  <span className="gb-menu__label">{t('hedefler.moveTo')}</span>
                  {others.map((s) => (
                    <button key={s.id} type="button" role="menuitem" onClick={() => { close(); actions.moveTo(todo, s.id); }}>
                      <Icon name="move" aria-hidden="true" /> <bdi>{s.title}</bdi>
                    </button>
                  ))}
                </>
              )}
              <button type="button" role="menuitem" className="is-danger" onClick={() => { close(); actions.remove(todo); }}>
                <Icon name="trash" aria-hidden="true" /> {t('hedefler.deleteTask')}
              </button>
            </div>
          )}
        </Pop>
        {sortable && (
          <span className="gb-row__handle" {...attributes} {...listeners} tabIndex={-1} aria-hidden="true" title={t('hedefler.dragHint')}>
            <Icon name="grip" />
          </span>
        )}
      </span>
    </li>
  );
};

/** One stage: its head (fold, rename, done, delete), its tasks and "Görev ekle". */
const GoalStage: React.FC<StageProps> = ({
  stage,
  index,
  folded,
  onFold,
  stages,
  today,
  allTags,
  actions,
  editingId,
  setEditingId,
  adding,
  setAdding,
  onAdd,
  onRename,
  onToggleDone,
  onDelete,
  tagFilter,
  highlighted,
  openTodoId,
}) => {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: `stage:${stage.id}`, data: { stageId: stage.id } });
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(stage.title);
  const rows = stageRows(stage);
  const keep = (td: Todo) => !tagFilter || Boolean(td.tags?.some((x) => x.id === tagFilter));
  const open = rows.open.filter(keep);
  const done = rows.done.filter(keep);
  const total = (stage.todos ?? []).length;
  const headId = `gb-stage-${stage.id}`;

  const commitName = () => {
    setRenaming(false);
    const title = name.trim();
    if (title && title !== stage.title) onRename(stage, title);
  };

  return (
    <section
      className={`gb-stage${highlighted ? ' is-highlighted' : ''}${isOver ? ' is-over' : ''}`}
      aria-labelledby={headId}
      data-stage-id={stage.id}
      ref={setNodeRef}
    >
      <div className="gb-stage__head">
        {renaming ? (
          <input
            className="gb-stage__input"
            autoFocus
            aria-label={t('hedefler.stageName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName();
              if (e.key === 'Escape') {
                setName(stage.title);
                setRenaming(false);
              }
            }}
          />
        ) : (
          <button type="button" className="gb-stage__toggle" aria-expanded={!folded} onClick={() => onFold(!folded)}>
            <Icon name={folded ? 'chevronRight' : 'chevronDown'} className="gb-stage__chev" aria-hidden="true" />
            <span className="gb-stage__name" id={headId}>
              {index + 1} · <bdi>{stage.title}</bdi>
            </span>
            {stage.completed && <span className="gb-stage__badge">{t('hedefler.stageDone')}</span>}
          </button>
        )}
        <span className="gb-stage__count">
          {rows.done.length}/{total}
        </span>
        <Pop label={t('hedefler.stageMenu', { title: stage.title })} trigger={<Icon name="more" aria-hidden="true" />} align="end">
          {(close) => (
            <div className="gb-menu" role="menu">
              <button type="button" role="menuitem" onClick={() => { close(); setName(stage.title); setRenaming(true); }}>
                <Icon name="edit" aria-hidden="true" /> {t('hedefler.rename')}
              </button>
              <button type="button" role="menuitem" onClick={() => { close(); onToggleDone(stage); }}>
                <Icon name={stage.completed ? 'undo' : 'circleCheck'} aria-hidden="true" />{' '}
                {t(stage.completed ? 'hedefler.stageReopen' : 'hedefler.stageComplete')}
              </button>
              <button type="button" role="menuitem" className="is-danger" onClick={() => { close(); onDelete(stage); }}>
                <Icon name="trash" aria-hidden="true" /> {t('hedefler.stageDelete')}
              </button>
            </div>
          )}
        </Pop>
      </div>
      {!folded && (
        <>
          <SortableContext items={open.map((x) => x.id)} strategy={verticalListSortingStrategy}>
            <ul className="gb-rows" aria-labelledby={headId}>
              {open.map((todo) => (
                <TaskLine
                  key={todo.id}
                  todo={todo}
                  stage={stage}
                  stages={stages}
                  today={today}
                  allTags={allTags}
                  actions={actions}
                  editing={editingId === todo.id}
                  setEditingId={setEditingId}
                  sortable={!tagFilter}
                  isOpen={openTodoId === todo.id}
                />
              ))}
              {done.map((todo) => (
                <TaskLine
                  key={todo.id}
                  todo={todo}
                  stage={stage}
                  stages={stages}
                  today={today}
                  allTags={allTags}
                  actions={actions}
                  editing={editingId === todo.id}
                  setEditingId={setEditingId}
                  sortable={false}
                  isOpen={openTodoId === todo.id}
                />
              ))}
            </ul>
          </SortableContext>
          {total === 0 && <p className="gb-stage__empty">{t('hedefler.emptyStage')}</p>}
          <div className="gb-stage__add">
            <AddRow
              label={t('bugun.add')}
              placeholder={t('bugun.addHint')}
              open={adding}
              onOpenChange={setAdding}
              onSubmit={(title) => onAdd(stage, title)}
            />
          </div>
        </>
      )}
    </section>
  );
};

export default GoalStage;
